import api from './api';

export interface PlanPrice {
  amount_cents: number;
  currency: string;
  display: string;
}

export interface PlanPricesResponse {
  pro: PlanPrice;
  premium: PlanPrice;
}

const CACHE_KEY = 'plan-prices.last-good';

interface CachedPayload {
  data: PlanPricesResponse;
  fetchedAt: number;
}

function isPlanPrice(p: unknown): p is PlanPrice {
  return (
    typeof p === 'object' &&
    p !== null &&
    typeof (p as PlanPrice).display === 'string' &&
    typeof (p as PlanPrice).amount_cents === 'number' &&
    typeof (p as PlanPrice).currency === 'string'
  );
}

export function readCachedPlanPrices(): PlanPricesResponse | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload | null;
    if (!parsed || !isPlanPrice(parsed.data?.pro) || !isPlanPrice(parsed.data?.premium)) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCachedPlanPrices(data: PlanPricesResponse): void {
  try {
    const payload: CachedPayload = { data, fetchedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be disabled (private mode, quota) — silently skip;
    // the next successful fetch will retry the write.
  }
}

export async function fetchPlanPrices(): Promise<PlanPricesResponse> {
  try {
    const data = await api.request<PlanPricesResponse>('/api/v1/payments/plans/prices');
    writeCachedPlanPrices(data);
    return data;
  } catch (err) {
    const cached = readCachedPlanPrices();
    if (cached) return cached;
    throw err;
  }
}

// Exposed for tests to reset state between cases.
export const __PLAN_PRICES_CACHE_KEY__ = CACHE_KEY;
